import ConcentrationNeeded from './ConcentrationNeeded'
import { fragranceDisplayName } from '../../lib/fragranceTitle'
import FavoriteButton from './FavoriteButton'
import { useNavigate } from 'react-router-dom'
import FragranceImage from './FragranceImage'
import { scentFamilyTokens } from '../../lib/displayLabels'
import { accordStandoutView } from '../../lib/accordStandouts'
import {
  CARD_STAT_KINDS,
  bottleCardStats,
  primaryStatText,
  secondaryLineText,
} from '../../lib/fragranceCardStats'

// The summary card's footer is narrow: long-form concentrations shrink to the
// short form a collector reads at a glance. Display only; the detail page and
// the title attribute keep the full label.
const SHORT_CONCENTRATION = {
  'eau de parfum': 'EDP',
  'eau de toilette': 'EDT',
  'eau de cologne': 'EDC',
  'extrait de parfum': 'Extrait',
}
const shortConcentration = value => SHORT_CONCENTRATION[String(value || '').trim().toLowerCase()] || value

// A grouped card opens its first active row, so a finished representative never
// hides a sibling that is still on the shelf.
export function openTargetId(frag) {
  const active = (frag.members || []).find(m => !m.finished_at && !m.given_away)
  return active?.id ?? frag.id
}

function StatIcon({ kind }) {
  if (kind === CARD_STAT_KINDS.compliments) {
    return (
      <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
        <path d="M10 17.2 3.4 11A4.6 4.6 0 0 1 10 4.7 4.6 4.6 0 0 1 16.6 11L10 17.2Z" />
      </svg>
    )
  }
  if (kind === CARD_STAT_KINDS.lastWorn) {
    return (
      <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
        <circle cx="10" cy="10" r="7" /><path d="M10 6v4l2.7 1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }
  if (kind === CARD_STAT_KINDS.bottles) {
    return (
      <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="M8 2.8h4v3l1.8 2.4v7.7c0 .8-.7 1.4-1.5 1.4H7.7c-.8 0-1.5-.6-1.5-1.4V8.2L8 5.8v-3Z" strokeLinejoin="round" />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
      <path d="M15.3 6.3A6.6 6.6 0 1 1 10 3.4" strokeLinecap="round" /><path d="M12.2 2.8h3.5v3.5M15.5 3l-3.8 3.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function PrimaryStatSlot({ stat, secondaryLine, fixed = false, compact = false }) {
  const secondary = secondaryLineText(secondaryLine)
  return (
    <div className={`${compact ? 'h-9 pt-1.5' : `${fixed ? 'h-10' : 'min-h-[2.5rem]'} pt-2`} overflow-hidden border-t border-white/[0.06]`}>
      <p className={`font-data flex items-center ${compact ? 'gap-1 text-[10px]' : 'gap-1.5 text-[11px]'} font-semibold leading-none text-cream/70`}>
        <span className="text-gold/75"><StatIcon kind={stat?.kind} /></span>
        <span>{primaryStatText(stat)}</span>
      </p>
      {secondary && <p className={`${compact ? 'text-[9px]' : 'text-[10px]'} mt-1 truncate leading-none text-cream/35`}>{secondary}</p>}
    </div>
  )
}

export default function FragranceCard({
  frag,
  shelfAccords,
  variant = 'collection',
  eyebrow,
  primaryStat,
  secondaryLine = null,
  interactive = true,
  className = '',
  onActivate,
  onQuickLog,
}) {
  const nav = useNavigate()
  const multiConc = (frag.concentrations?.length ?? 0) > 1
  const inventoryCount = Number(frag.inventory_count ?? frag.bottle_count ?? 1)
  const canQuickLog = onQuickLog && !frag.given_away && !frag.finished
  const soloConc = multiConc ? null : (frag.concentrations?.[0] ?? frag.concentration)
  // From the notes (the legacy scent_family string put Woody on 191 of Rob's 344
  // labelled cards). With the shelf passed in, the accord that is rare in this
  // collection leads in gold. The legacy family shows only when we hold no notes,
  // the same rule as the detail card's accordChips.
  const noteAccords = accordStandoutView(frag, shelfAccords, 3).chips
  const cardAccords = noteAccords.length
    ? noteAccords
    : scentFamilyTokens(frag.scent_family).slice(0, 3).map(label => ({ key: label, label, standout: false }))
  const resolvedStats = primaryStat
    ? { primaryStat, secondaryLine }
    : bottleCardStats(inventoryCount)
  const activate = () => {
    if (!interactive) return
    if (onActivate) onActivate(frag)
    else nav(`/fragrance/${openTargetId(frag)}`)
  }
  const isHomeCard = variant === 'home' || variant === 'homeCompact' || variant === 'feature'
  const interactionProps = interactive ? {
    onClick: activate,
    ...(isHomeCard ? {
      role: 'button',
      tabIndex: 0,
      onKeyDown: event => {
        if (event.target !== event.currentTarget) return
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          activate()
        }
      },
    } : {}),
  } : {}

  if (isHomeCard) {
    const feature = variant === 'feature'
    const compact = variant === 'homeCompact'
    return (
      <article
        {...interactionProps}
        className={`card group relative flex flex-col ${feature ? 'h-full' : compact ? 'h-full min-w-0' : 'h-[19rem] w-36 shrink-0'} ${interactive ? 'cursor-pointer' : ''} ${className}`}
      >
        <div className={feature ? 'p-3' : compact ? 'h-full p-1.5' : 'h-full p-2.5'}>
          {feature && eyebrow && (
            <p className={`${feature ? 'text-[10px]' : 'text-[9px]'} mb-2 font-bold uppercase tracking-[0.12em] text-gold/70`}>
              {eyebrow}
            </p>
          )}
          {!feature && (
            <p className={`${compact ? 'mb-1 h-6 text-[8px] leading-3 tracking-[0.08em]' : 'mb-2 h-7 text-[9px] leading-[0.875rem] tracking-[0.12em]'} overflow-hidden font-bold uppercase text-gold/70`}>
              {eyebrow}
            </p>
          )}
          {/* Home rail geometry is owned here, not by each caller. The explicit
              wrapper keeps every image frame at exactly the same dimensions;
              FragranceImage fills it with the shared contain treatment. */}
          {feature ? (
            <FragranceImage
              fragrance={frag}
              className="aspect-[61/76] shrink-0 rounded-xl"
              imgClassName="p-3 transition-opacity duration-300 group-hover:opacity-95"
            />
          ) : (
            <div className={`${compact ? 'aspect-[4/5]' : 'aspect-[61/76]'} w-full shrink-0`} data-home-card-image-frame>
              <FragranceImage
                fragrance={frag}
                fill
                imgClassName={`${compact ? 'p-1.5' : 'p-3'} transition-opacity duration-300 group-hover:opacity-95`}
              />
            </div>
          )}
          <div className={feature ? 'mt-3' : compact ? 'mt-1.5 h-9 overflow-hidden' : 'mt-2.5 h-[2.625rem] overflow-hidden'}>
            <p className={`${feature ? 'font-body text-lg font-medium' : compact ? 'line-clamp-2 text-[11px] font-semibold' : 'line-clamp-2 text-xs font-semibold'} leading-tight text-cream`}>{fragranceDisplayName(frag)}</p>
            <p className={`${feature ? 'mt-1 text-xs' : compact ? 'mt-0.5 text-[9px]' : 'mt-0.5 text-[10px]'} line-clamp-1 text-cream/45`}>{frag.house}</p>
          </div>
          <div className={feature ? 'mt-3' : compact ? 'mt-1.5' : 'mt-2.5'}>
            <PrimaryStatSlot
              stat={resolvedStats.primaryStat}
              secondaryLine={resolvedStats.secondaryLine}
              fixed={!feature}
              compact={compact}
            />
          </div>
        </div>
      </article>
    )
  }

  // Collection summary card (Rob 2026-10-02): image, identity, accords,
  // inspired-by, concentration, favorite and SOTD — nothing else. Season,
  // size, year, bottle count and the overflow menu live on the detail page;
  // tapping the card (image included) goes there for every other option.
  // Resolver-backed values first, the same source the concentration filter reads.
  const cardConcentrations = frag.resolved_concentrations?.length
    ? frag.resolved_concentrations
    : (multiConc ? frag.concentrations : [soloConc].filter(Boolean))
  const concentrationText = cardConcentrations.join(' · ')
  const concentrationShort = cardConcentrations.map(shortConcentration).join(' · ')

  return (
    <div
      {...interactionProps}
      className={`card group relative flex h-full flex-col ${interactive ? 'cursor-pointer' : ''} ${frag.given_away || frag.finished ? 'opacity-55' : ''} ${frag.finished ? 'border-sage/30' : ''} ${className}`}
      data-collection-card
    >
      {/* The image is the card's native control: keyboard users open the fragrance here. */}
      <button
        type="button"
        aria-label={`Open ${fragranceDisplayName(frag)}`}
        disabled={!interactive}
        onClick={e => { e.stopPropagation(); activate() }}
        className="relative block aspect-square w-full overflow-hidden rounded-lg text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold"
        data-collection-card-open
      >
        <FragranceImage
          fragrance={frag}
          fill
          imgClassName="transition-opacity duration-300 group-hover:opacity-95"
        />
        {frag.given_away && (
          <div className="absolute inset-0 bg-charcoal-900/70 flex items-center justify-center">
            <span className="text-xs text-cream/50 font-medium tracking-widest uppercase">Given Away</span>
          </div>
        )}
        {frag.finished && (
          <div className="absolute inset-0 bg-charcoal-900/70 flex items-center justify-center">
            <span className="text-xs text-sage font-medium tracking-widest uppercase">Finished</span>
          </div>
        )}
      </button>

      <div className="flex flex-1 flex-col px-2.5 pt-2">
        <p className="line-clamp-2 break-words font-body text-sm font-semibold leading-[1.2] text-cream">{fragranceDisplayName(frag)}</p>
        <p className="mt-0.5 line-clamp-1 text-xs text-cream/50">{frag.house}</p>
        {cardAccords.length > 0 && (
          <p className="mt-0.5 line-clamp-1 text-[10px] text-cream/35">
            {cardAccords.map((chip, i) => (
              <span key={chip.key}>
                {i > 0 && ' · '}
                {chip.standout ? <span className="font-semibold text-gold/90" data-standout="true">{chip.label}</span> : chip.label}
              </span>
            ))}
          </p>
        )}
        {frag.inspired_by && (
          <p className="mt-1 truncate text-[10px] text-cream/50">
            <span className="mr-1 text-gold/60">✦</span>Inspired by {frag.inspired_by}
          </p>
        )}

        {/* One footer row: concentration left, favorite + SOTD right. */}
        <div className="mt-auto flex min-h-11 items-center gap-1">
          <span className="mr-auto flex min-w-0 items-center gap-1 text-[10px] leading-none text-cream/40">
            <ConcentrationNeeded row={frag} />
            {concentrationText && <span className="truncate" title={concentrationText}>{concentrationShort}</span>}
          </span>
          {frag.user_id && <FavoriteButton fragrance={frag} compact />}
          {canQuickLog && (
            <button
              type="button"
              aria-label={`Log ${frag.name} as scent of the day`}
              onClick={e => { e.stopPropagation(); onQuickLog(frag) }}
              className="group/sotd -mr-1 flex min-h-11 items-center justify-center px-1"
            >
              <span className="inline-flex rounded-full border border-gold/25 bg-gold/[0.04] px-2 py-1 text-[10px] font-semibold uppercase leading-none tracking-[0.1em] text-gold/80 transition-colors group-hover/sotd:border-gold/45 group-hover/sotd:bg-gold/10 group-hover/sotd:text-gold">
                SOTD
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
